import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  LockReset as ResetIcon,
} from "@mui/icons-material";
import { TeamMember } from "../../services/api";
import { getAvatarColor, getInitials } from "../../utils/avatarUtils";
import { EmptyState } from "../shared";
import { lightTheme } from "../../styles/theme";
import {
  MANAGEMENT_ACTION_ICON_BUTTON_SIZE,
  MANAGEMENT_ACTION_ICON_FONT_SIZE,
  MANAGEMENT_ACTION_ROW_SX,
} from "../../styles/managementCardActions";
import {
  MANAGEMENT_CARD_HEADER_FONT_SIZE,
  MANAGEMENT_CARD_META_FONT_SIZE,
  MANAGEMENT_CARD_TITLE_FONT_SIZE,
} from "../../styles/managementCardTypography";
import {
  MANAGEMENT_AVATAR_FONT_SIZE,
  MANAGEMENT_AVATAR_SIZE,
} from "../../styles/managementShared";

interface Circle {
  circleId: string;
  name: string;
  description: string;
  isPublic: boolean;
  isSubCircle: boolean;
  parentCircle?: string;
  memberCount: number;
  createdAt: string;
  createdBy: string;
  circleApproved: boolean;
  logo: string;
  type: string;
}

interface TeamListProps {
  teamMembers: TeamMember[];
  circles: Circle[];
  onDeleteMember?: (member: TeamMember) => void;
  canDeleteMember?: (member: TeamMember) => boolean;
  onEditMember?: (member: TeamMember) => void;
  onResetPin?: (member: TeamMember) => void;
  onAddMember?: () => void;
  isMobile: boolean;
  isLoading: boolean;
}

const DESKTOP_GRID_COLUMNS =
  "minmax(0, 2fr) minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 0.8fr)";

const createStyles = () => ({
  // Header styles
  headerCell: {
    fontWeight: 600,
    color: "text.secondary",
    textTransform: "uppercase" as const,
    fontSize: MANAGEMENT_CARD_HEADER_FONT_SIZE,
    letterSpacing: "0.5px",
  },

  // Card styles
  mobileCard: {
    p: 2,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    boxShadow: "none",
    display: "flex",
    alignItems: "center",
    flexDirection: "row" as const,
    gap: 2,
    minHeight: 56,
    position: "relative" as const,
  },
  mobileCardContent: {
    flex: 1,
    minWidth: 0,
    pr: 14,
  },
  mobileCardName: {
    fontSize: MANAGEMENT_CARD_TITLE_FONT_SIZE,
  },
  mobileCardUsername: {
    fontSize: MANAGEMENT_CARD_META_FONT_SIZE,
  },
  mobileCardDate: {
    fontSize: "0.625rem", // 10px
  },
  mobileDateContainer: {
    width: 80,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    pr: 1,
  },
});

// Utility functions
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const TeamList: React.FC<TeamListProps> = React.memo(
  ({
    teamMembers,
    circles,
    onDeleteMember,
    canDeleteMember,
    onEditMember,
    onResetPin,
    onAddMember,
    isMobile,
    isLoading,
  }) => {
    const styles = useMemo(() => createStyles(), []);

    // Create a mapping from circle ID to circle name
    const circleIdToName = useMemo(() => {
      const mapping: Record<string, string> = {};
      circles.forEach((circle) => {
        mapping[circle.circleId] = circle.name;
      });
      return mapping;
    }, [circles]);

    // Empty state
    if (teamMembers.length === 0 && !isLoading) {
      if (!onAddMember) return null;
      
      return (
        <EmptyState
          title="Build Your Team"
          description="Add team members to help manage your organization. Each member can have different access levels and permissions."
          imageSrc="/teams/team.webp"
          imageAlt="Team members illustration"
          buttonText="Add Your First Team Member"
          onButtonClick={onAddMember}
          benefits={[
            "Share responsibilities",
            "Assign admin roles",
            "Control permissions",
            "Keep operations secure",
          ]}
          benefitsTitle="Why add team members?"
          benefitsShowCheckmarks={false}
          howItWorks={[
            "Invite team members via email or phone",
            "Assign access levels",
            "Grant circle access permissions",
          ]}
          howItWorksTitle="How it works:"
        />
      );
    }

    if (isMobile) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {teamMembers.map((member) => (
            <Paper key={member.username} elevation={0} sx={styles.mobileCard}>
              <Avatar
                sx={{
                  bgcolor: getAvatarColor(member.displayName),
                    width: MANAGEMENT_AVATAR_SIZE,
                    height: MANAGEMENT_AVATAR_SIZE,
                    fontSize: MANAGEMENT_AVATAR_FONT_SIZE,
                }}
              >
                {getInitials(member.displayName)}
              </Avatar>
              <Box sx={styles.mobileCardContent}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    noWrap
                    sx={styles.mobileCardName}
                  >
                    {member.displayName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={styles.mobileCardUsername}
                  >
                    {member.username}
                  </Typography>
                </Box>
                {member.phoneNumber && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ fontSize: "0.75rem", mt: 0.5 }}
                  >
                    {member.phoneNumber}
                  </Typography>
                )}
                {member.circleIds && member.circleIds.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    {member.circleIds.slice(0, 2).map((circleId) => (
                      <Chip
                        key={circleId}
                        label={circleIdToName[circleId] || circleId}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "0.6rem",
                          height: 18,
                          maxWidth: "80px",
                        }}
                      />
                    ))}
                    {member.circleIds.length > 2 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.6rem", alignSelf: "center" }}
                      >
                        +{member.circleIds.length - 2}
                      </Typography>
                    )}
                  </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                  <Chip
                    label={member.accessLevel}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.65rem", height: 20 }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bottom: 8,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={styles.mobileCardDate}
                >
                  {formatDate(member.createdAt)}
                </Typography>
                <Box sx={MANAGEMENT_ACTION_ROW_SX}>
                  {onEditMember && (
                    <IconButton
                      onClick={() => onEditMember(member)}
                      size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                      sx={{ color: lightTheme.colors.primary }}
                      aria-label={`Edit ${member.displayName}`}
                    >
                      <EditIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                    </IconButton>
                  )}
                  {onResetPin && (
                    <IconButton
                      onClick={() => onResetPin(member)}
                      size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                      sx={{ color: lightTheme.colors.warning }}
                      aria-label={`Reset PIN for ${member.displayName}`}
                    >
                      <ResetIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                    </IconButton>
                  )}
                  {onDeleteMember && (canDeleteMember?.(member) ?? true) && (
                    <IconButton
                      onClick={() => onDeleteMember(member)}
                      size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                      sx={{ color: lightTheme.colors.error }}
                      aria-label={`Remove ${member.displayName}`}
                    >
                      <DeleteIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      );
    }

    return (
      <Box>
        {/* Header Row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: DESKTOP_GRID_COLUMNS,
            gap: 2,
            px: 3,
            py: 1,
            mb: 1,
          }}
        >
          <Typography sx={styles.headerCell}>Name</Typography>
          <Typography sx={styles.headerCell}>Username</Typography>
          <Typography sx={styles.headerCell}>Phone</Typography>
          <Typography sx={styles.headerCell}>Circles</Typography>
          <Typography sx={styles.headerCell}>Date Joined</Typography>
          <Box></Box>
        </Box>

        {/* Team Member Rows */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {teamMembers.map((member) => (
            <Paper
              key={member.username}
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: DESKTOP_GRID_COLUMNS,
                  gap: 2,
                  px: 3,
                  py: 2,
                  alignItems: "center",
                }}
              >
                {/* Name Column */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: getAvatarColor(member.displayName),
                      width: MANAGEMENT_AVATAR_SIZE,
                      height: MANAGEMENT_AVATAR_SIZE,
                      fontSize: MANAGEMENT_AVATAR_FONT_SIZE,
                    }}
                  >
                    {getInitials(member.displayName)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      noWrap
                      sx={{ fontSize: MANAGEMENT_CARD_TITLE_FONT_SIZE }}
                    >
                      {member.displayName}
                    </Typography>
                    <Chip
                      label={member.accessLevel}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.65rem",
                        height: 18,
                        mt: 0.5,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Box>
                </Box>

                {/* Username Column */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  sx={{ fontSize: MANAGEMENT_CARD_META_FONT_SIZE }}
                >
                  {member.username}
                </Typography>

                {/* Phone Column */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  sx={{ fontSize: MANAGEMENT_CARD_META_FONT_SIZE }}
                >
                  {member.phoneNumber || "—"}
                  {member.phoneNumber && member.phoneVerified && " ✓"}
                </Typography>

                {/* Circles Column */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {member.circleIds && member.circleIds.length > 0 ? (
                    member.circleIds.map((circleId) => (
                      <Chip
                        key={circleId}
                        label={circleIdToName[circleId] || circleId}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "0.65rem",
                          height: 20,
                          maxWidth: "120px",
                        }}
                      />
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: MANAGEMENT_CARD_META_FONT_SIZE }}
                    >
                      —
                    </Typography>
                  )}
                </Box>

                {/* Date Joined Column */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: MANAGEMENT_CARD_META_FONT_SIZE }}
                >
                  {formatDate(member.createdAt)}
                </Typography>

                {/* Actions Column — matches Circles.tsx circle row actions */}
                <Box className="action-buttons" sx={MANAGEMENT_ACTION_ROW_SX}>
                  {onEditMember && (
                    <IconButton
                      onClick={() => onEditMember(member)}
                      size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                      sx={{ color: lightTheme.colors.primary }}
                      aria-label={`Edit ${member.displayName}`}
                    >
                      <EditIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                    </IconButton>
                  )}
                  {onResetPin && (
                    <IconButton
                      onClick={() => onResetPin(member)}
                      size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                      sx={{ color: lightTheme.colors.warning }}
                      aria-label={`Reset PIN for ${member.displayName}`}
                    >
                      <ResetIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                    </IconButton>
                  )}
                  {onDeleteMember && (canDeleteMember?.(member) ?? true) && (
                    <IconButton
                      onClick={() => onDeleteMember(member)}
                      size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                      sx={{ color: lightTheme.colors.error }}
                      aria-label={`Remove ${member.displayName}`}
                    >
                      <DeleteIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }
);

TeamList.displayName = "TeamList";

export default TeamList;
